import { useCallback, useEffect, useState } from 'react';
import { BellRing, ChevronRight, Pin } from 'lucide-react';

import { FeatureLoading } from '../feature-skeleton';
import { BoardDetailPanel } from './board-detail-panel';
import {
  orderedPosts,
  postDate,
  readerAuthority,
  type SafetyBoardAuthority,
  type SafetyBoardCommand,
} from '../safety-board/model';
import { createSafetyBoardRepository } from '../safety-board/repository';
import { useSafetyBoardSite } from '../safety-board/site-context';
import { SafetyBoardCategoryChip } from '../safety-board/board-rows';
import { HOME_BOARD_ROWS, useSafetyBoardPage } from '../safety-board/use-safety-board-page';
import { SafetyBoardWorkspace } from '../safety-board/safety-board-workspace';

import './briefing.css';

/**
 * 안전보건게시판 -- the work home's board.
 *
 * One list call on open. That page carries everything a row and the detail panel render, so
 * opening a post costs no further read; the only other call this card makes is the read receipt
 * the panel sends, and a row un-bolds from that receipt rather than ahead of it.
 *
 * The highest-priority item is emphasized and five rows stay compact. Category presentation
 * comes from the server settings projection rather than a client-owned palette.
 */
export function BriefingBoard({
  authority,
  owner,
  onSettled,
}: {
  authority: SafetyBoardAuthority;
  owner: { uid: string; contractorId: string | null };
  onSettled?: () => void;
}) {
  const { snapshot, reload, acceptReadReceipt } = useSafetyBoardPage(HOME_BOARD_ROWS);
  const siteId = useSafetyBoardSite();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [archiveOpen, setArchiveOpen] = useState(false);
  useEffect(()=>{if(snapshot.state!=='loading')onSettled?.();},[snapshot.state,onSettled]);

  const page = snapshot.state === 'ready' ? snapshot.page : null;
  const settings = page?.settings ?? null;
  const selected = page?.items.find((item) => item.boardId === selectedId) ?? null;
  const ordered = orderedPosts(page?.items ?? []);
  const featured = ordered[0] ?? null;
  const compact = ordered.slice(1, 6);
  const projectedAuthority = settings?.authority.canManage ? authority : readerAuthority;

  const runCommand = useCallback(
    async (command: SafetyBoardCommand) => {
      await (await createSafetyBoardRepository(siteId)).execute(command);
      await reload();
    },
    [reload, siteId],
  );

  return (
    <>
      <section className="briefing-board" aria-labelledby="briefing-board-title">
        <header>
          <div>
            <span className="eyebrow">SAFETY BRIEFING</span>
            <h2 id="briefing-board-title">안전보건게시판</h2>
          </div>
          <div className="briefing-board-aside">
            {page && (
              <small>
                {page.unreadVisible > 0 ? `${page.unreadVisible}건` : '새 전달사항 없음'}
              </small>
            )}
            <button
              type="button"
              className="briefing-board-more"
              onClick={() => setArchiveOpen(true)}
            >
              더보기 <ChevronRight size={14} />
            </button>
          </div>
        </header>
        {snapshot.state === 'loading' ? (
          <div className="briefing-board-loading" role="status" aria-label="게시판 불러오는 중">
            <FeatureLoading label="게시판 불러오는 중" />
          </div>
        ) : snapshot.state === 'unavailable' ? (
          <div className="briefing-board-empty">
            <BellRing size={18} />
            <span>게시판을 불러오지 못했습니다.</span>
          </div>
        ) : snapshot.page.items.length === 0 ? (
          <div className="briefing-board-empty">
            <BellRing size={18} />
            <span>게시글이 없습니다.</span>
          </div>
        ) : (
          <div className="briefing-board-list">
            {featured && (
              <button
                key={featured.boardId}
                type="button"
                className="briefing-board-featured"
                data-board-id={featured.boardId}
                data-read={String(featured.read)}
                data-emphasis={featured.emphasis}
                onClick={() => setSelectedId(featured.boardId)}
              >
                <span className="briefing-row-pin">
                  {featured.pinned ? <Pin size={12} aria-label="고정" /> : null}
                </span>
                <SafetyBoardCategoryChip post={featured} categories={settings?.categories ?? []} />
                <strong data-title-color={featured.titleColor}>{featured.title}</strong>
                <small>{postDate(featured)}</small>
              </button>
            )}
            {compact.map((post) => (
              <button
                key={post.boardId}
                type="button"
                data-board-id={post.boardId}
                data-read={String(post.read)}
                onClick={() => setSelectedId(post.boardId)}
              >
                <SafetyBoardCategoryChip post={post} categories={settings?.categories ?? []} />
                <strong data-title-color={post.titleColor}>{post.title}</strong>
                <small>{postDate(post)}</small>
              </button>
            ))}
          </div>
        )}
      </section>

      {selected && (
        <BoardDetailPanel
          post={selected}
          settings={settings}
          authority={projectedAuthority}
          onClose={() => setSelectedId(null)}
          onReadReceipt={acceptReadReceipt}
          onCommand={runCommand}
        />
      )}
      {archiveOpen && <SafetyBoardWorkspace owner={owner} onClose={() => setArchiveOpen(false)} />}
    </>
  );
}
